import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  analisarEscadaApoiada,
  chaoSobEscada,
  localizarPontoNoMundo,
  parametrosDoObjeto,
  type AncoragemTabletop,
  type CenaTabletop,
  type ObjetoTabletop,
  type ResultadoEscadaApoiada,
  type VetorTabletop,
} from '../../dominio/tabletop'
import { carregarMotor, type EstadoMotor, type Rapier } from '../../fisica/motor'
import { simularAncoragemV, type ModoSimulacaoV, type QuadroSimulacaoV, type ResultadoSimulacaoV } from '../../fisica/simulacaoAncoragem'
import { simularEscadaApoiada, type ResultadoSimulacaoEscada } from '../../fisica/simulacaoEscada'

/**
 * As simulações de física do table top: estado, carregamento sob demanda do
 * motor e reprodução. Moram aqui, e não nos painéis, porque o painel fecha
 * quando o instrutor vai olhar a cena e o resultado não pode se perder junto.
 *
 * Um resultado vale para a montagem em que foi feito: mexeu em algo, a
 * simulação some e se refaz. Se o motor não carregar, a mesa continua pelo
 * desenho, sem física.
 */

export interface ParametrosSimulacaoEscada {
  readonly massaEscadaKg: number
  readonly massaCargaKg: number
  readonly posicaoCargaFracao: number
  readonly atritoPe: number
}

export const PARAMETROS_ESCADA_PADRAO: ParametrosSimulacaoEscada = {
  massaEscadaKg: 12,
  massaCargaKg: 90,
  posicaoCargaFracao: 0.5,
  atritoPe: 0.5,
}

export interface ResultadoEscadaNaMesa {
  readonly parametros: ParametrosSimulacaoEscada
  readonly anguloGraus: number
  readonly comprimentoM: number
  /** Inclinação do chão sob o pé na hora do teste (graus; sobe em direção à parede = positivo); 0 = chão plano. */
  readonly inclinacaoChaoGraus: number
  readonly analitico: ResultadoEscadaApoiada
  readonly simulado: ResultadoSimulacaoEscada
}

export interface SimulacaoAncoragemNaMesa {
  readonly idAncoragem: string
  readonly modo: ModoSimulacaoV
  readonly cedeIndice: 0 | 1 | null
  readonly fixacoes: readonly [VetorTabletop, VetorTabletop]
  readonly resultado: ResultadoSimulacaoV
}

export interface SimulacoesTabletop {
  readonly estadoMotor: EstadoMotor
  readonly escadas: Readonly<Record<string, ResultadoEscadaNaMesa>>
  simularEscada: (objeto: ObjetoTabletop, parametros: ParametrosSimulacaoEscada) => Promise<void>
  readonly ancoragem: SimulacaoAncoragemNaMesa | null
  readonly indiceQuadro: number
  readonly tocando: boolean
  /** O quadro em exibição e as fixações, para a cena 3D; `null` sem simulação. */
  readonly quadroAtual: { fixacoes: readonly [VetorTabletop, VetorTabletop]; quadro: QuadroSimulacaoV } | null
  simularAncoragem: (ancoragem: AncoragemTabletop, modo: ModoSimulacaoV, cedeIndice: 0 | 1 | null) => Promise<void>
  definirIndiceQuadro: (indice: number) => void
  alternarReproducao: () => void
  limparAncoragem: () => void
}

const SEM_SIMULACOES: SimulacoesTabletop = {
  estadoMotor: 'ocioso',
  escadas: {},
  simularEscada: async () => {},
  ancoragem: null,
  indiceQuadro: 0,
  tocando: false,
  quadroAtual: null,
  simularAncoragem: async () => {},
  definirIndiceQuadro: () => {},
  alternarReproducao: () => {},
  limparAncoragem: () => {},
}

const ContextoSimulacoes = createContext<SimulacoesTabletop>(SEM_SIMULACOES)
export const useSimulacoes = () => useContext(ContextoSimulacoes)

export function ProvedorSimulacoes({ valor, children }: { valor: SimulacoesTabletop; children: ReactNode }) {
  return <ContextoSimulacoes.Provider value={valor}>{children}</ContextoSimulacoes.Provider>
}

const QUADROS_POR_SEGUNDO = 30

export function useSimulacoesTabletop(cena: CenaTabletop): SimulacoesTabletop {
  const [estadoMotor, setEstadoMotor] = useState<EstadoMotor>('ocioso')
  const [escadas, setEscadas] = useState<Record<string, ResultadoEscadaNaMesa>>({})
  const [ancoragem, setAncoragem] = useState<SimulacaoAncoragemNaMesa | null>(null)
  const [indiceQuadro, setIndiceQuadro] = useState(0)
  const [tocando, setTocando] = useState(false)

  // a simulação vale para a montagem em que foi feita
  const cenaDaSimulacao = useRef<CenaTabletop | null>(null)
  useEffect(() => {
    if (cenaDaSimulacao.current && cenaDaSimulacao.current !== cena) {
      cenaDaSimulacao.current = null
      setEscadas({})
      setAncoragem(null)
      setTocando(false)
      setIndiceQuadro(0)
    }
  }, [cena])

  const comMotor = useCallback(async <T,>(executar: (R: Rapier) => T): Promise<T | null> => {
    setEstadoMotor((atual) => (atual === 'pronto' ? atual : 'carregando'))
    try {
      const R = await carregarMotor()
      setEstadoMotor('pronto')
      return executar(R)
    } catch {
      setEstadoMotor('indisponivel')
      return null
    }
  }, [])

  const simularEscada = useCallback(
    async (objeto: ObjetoTabletop, parametros: ParametrosSimulacaoEscada) => {
      const p = parametrosDoObjeto(objeto)
      const anguloGraus = p.anguloGraus!
      const comprimentoM = p.comprimentoM!
      // o terreno de verdade sob a escada: vira colisor na simulação e inclinação na fórmula
      const chao = chaoSobEscada(cena, objeto)
      const inclinacaoChaoGraus = chao?.inclinacaoGraus ?? 0
      const entrada = { anguloGraus, comprimentoM, ...parametros, inclinacaoChaoGraus, perfilChaoM: chao?.perfilM }
      const resultado = await comMotor((R) => ({
        parametros,
        anguloGraus,
        comprimentoM,
        inclinacaoChaoGraus,
        analitico: analisarEscadaApoiada(entrada),
        simulado: simularEscadaApoiada(R, entrada),
      }))
      if (!resultado) return
      cenaDaSimulacao.current = cena
      setEscadas((atual) => ({ ...atual, [objeto.id]: resultado }))
    },
    [cena, comMotor],
  )

  const simularAncoragem = useCallback(
    async (a: AncoragemTabletop, modo: ModoSimulacaoV, cedeIndice: 0 | 1 | null) => {
      if (a.tipo !== 'V') return
      const pontos = [...a.fixacoes, a.centro].map((r) => localizarPontoNoMundo(cena, r))
      if (pontos.some((p) => p === null)) return
      const [fixacaoA, fixacaoB, centro] = pontos as [VetorTabletop, VetorTabletop, VetorTabletop]
      const resultado = await comMotor((R) =>
        simularAncoragemV(R, {
          fixacaoA,
          fixacaoB,
          centro,
          massaKg: a.cargaKgf,
          modo,
          ...(cedeIndice === null ? {} : { cedeIndice, instanteCedeS: 1 }),
          // a cedência precisa de mais tempo: a massa cai e balança antes de parar
          ...(cedeIndice === null ? {} : { duracaoS: 25 }),
        }),
      )
      if (!resultado) return
      cenaDaSimulacao.current = cena
      setAncoragem({ idAncoragem: a.id, modo, cedeIndice, fixacoes: [fixacaoA, fixacaoB], resultado })
      setIndiceQuadro(resultado.quadros.length - 1)
      setTocando(false)
    },
    [cena, comMotor],
  )

  const quadros = ancoragem?.resultado.quadros.length ?? 0
  useEffect(() => {
    if (!tocando || quadros === 0) return
    const intervalo = window.setInterval(() => {
      setIndiceQuadro((atual) => {
        if (atual >= quadros - 1) {
          setTocando(false)
          return atual
        }
        return atual + 1
      })
    }, 1000 / QUADROS_POR_SEGUNDO)
    return () => window.clearInterval(intervalo)
  }, [tocando, quadros])

  const definirIndiceQuadro = useCallback((indice: number) => {
    setTocando(false)
    setIndiceQuadro(indice)
  }, [])
  const alternarReproducao = useCallback(() => {
    setTocando((atual) => {
      if (!atual) setIndiceQuadro((i) => (i >= quadros - 1 ? 0 : i))
      return !atual
    })
  }, [quadros])
  const limparAncoragem = useCallback(() => {
    setAncoragem(null)
    setTocando(false)
    setIndiceQuadro(0)
  }, [])

  const quadroAtual = useMemo(
    () => (ancoragem ? { fixacoes: ancoragem.fixacoes, quadro: ancoragem.resultado.quadros[Math.min(indiceQuadro, quadros - 1)]! } : null),
    [ancoragem, indiceQuadro, quadros],
  )

  return useMemo(
    () => ({
      estadoMotor,
      escadas,
      simularEscada,
      ancoragem,
      indiceQuadro,
      tocando,
      quadroAtual,
      simularAncoragem,
      definirIndiceQuadro,
      alternarReproducao,
      limparAncoragem,
    }),
    [alternarReproducao, ancoragem, definirIndiceQuadro, escadas, estadoMotor, indiceQuadro, limparAncoragem, quadroAtual, simularAncoragem, simularEscada, tocando],
  )
}
